/**
 * =============================================================================
 * useReleaseFlags HOOK (ADR-025 blueprint §3.5 / M17 — B-1)
 * =============================================================================
 *
 * Reads the tenant's SERVER-EVALUATED release flags off the shared
 * tenant-settings read (`GET /api/admin/settings` — the frozen §3.5 delivery
 * vehicle; no session-bootstrap endpoint exists). The client NEVER decides a
 * flag: this hook only parses the server's decision, and it parses it
 * FAIL-CLOSED via the shared `resolveReleaseFlags` — a fetch failure, an
 * error envelope, or a missing/malformed `releaseFlags` field (e.g. an older
 * worker deploy) all resolve to every-flag-OFF, landing the operator on the
 * prior (legacy) surface rather than throwing or guessing.
 *
 * AdminApp's TenantSettingsProvider owns one authenticated full-settings GET
 * per app load; the unauthenticated gate still performs zero requests (M1).
 * This hook consumes that shared cache. Its private read exists only as a
 * fail-closed fallback outside the AdminApp composition.
 * =============================================================================
 */

import { useEffect, useState } from 'react';
import {
  DEFAULT_RELEASE_FLAGS,
  resolveReleaseFlags,
  type ReleaseFlags,
} from '@find-my-part/types';
import { adminApi } from '../api/client';
import { useTenantSettings } from '../providers/TenantSettingsProvider';

export interface UseReleaseFlagsResult {
  /**
   * 'loading' while the (token-gated) read is in flight; 'ready' once the
   * flags are decided — including every failure path (fail-closed OFF).
   */
  status: 'loading' | 'ready';
  /** Effective flags. Trustworthy only when `status === 'ready'`. */
  flags: ReleaseFlags;
}

export function useReleaseFlags(token: string | null): UseReleaseFlagsResult {
  const shared = useTenantSettings();
  const [result, setResult] = useState<UseReleaseFlagsResult>(() => ({
    // No token → nothing to read (and nothing may be fetched): immediately
    // ready with every flag OFF.
    status: token ? 'loading' : 'ready',
    flags: { ...DEFAULT_RELEASE_FLAGS },
  }));

  useEffect(() => {
    if (shared) return undefined;
    if (!token) {
      return undefined;
    }

    let cancelled = false;

    void (async () => {
      let flags: ReleaseFlags;
      try {
        const response = await adminApi.settings.get(token);
        // Fail-closed either way: a success envelope resolves its (possibly
        // absent/malformed) releaseFlags strictly; an error envelope resolves
        // to defaults. resolveReleaseFlags never throws.
        flags = response.success
          ? resolveReleaseFlags(response.data.releaseFlags)
          : { ...DEFAULT_RELEASE_FLAGS };
      } catch {
        // apiRequest only rejects on caller aborts; belt-and-braces anyway.
        flags = { ...DEFAULT_RELEASE_FLAGS };
      }
      if (!cancelled) {
        setResult({ status: 'ready', flags });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [shared, token]);

  if (shared) {
    return {
      status: shared.status === 'loading' ? 'loading' : 'ready',
      flags:
        shared.status === 'ready' && shared.settings
          ? resolveReleaseFlags(shared.settings.releaseFlags)
          : { ...DEFAULT_RELEASE_FLAGS },
    };
  }

  return result;
}
